'use client';

import { useSMID } from '@hc-frontend/core-ui';
import { buildSmidContext } from '@hc-frontend/third-party-snowplow';
import type { SelfDescribingJson } from '@snowplow/browser-tracker';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLayoutEffect, useMemo, useState } from 'react';

const SMID_URL_PARAM_KEY = 'smid';
const SMID_FOOTER_DOM_SELECTOR = '[data-hc-smid]';

/**
 * Reads the compliance SMID currently rendered in the page footer directly
 * from the DOM, via the `data-hc-smid` marker (see `FooterBottomLeftContent`
 * in core-ui's `footer.tsx` and `UnstyledSMID`). This is the fallback for
 * pages whose footer never wires up `SMIDProvider` — the DOM is the one
 * place the resolved value is guaranteed to be present regardless of which
 * upstream mechanism produced it (Builder.io content, a hardcoded
 * per-template constant, or a server-resolved page identifier).
 */
function readSmidFromFooterDom(): string | null {
  // Only ever invoked from inside a useLayoutEffect callback, which React
  // never runs during server rendering, so `document` is always defined here.
  const text = document
    .querySelector(SMID_FOOTER_DOM_SELECTOR)
    ?.textContent?.trim();

  return text || null;
}

/**
 * Resolves the `smid` Snowplow context from whatever SMID sources are
 * available at render time: the `smid` URL query parameter on the current
 * page, and the compliance SMID currently rendered in the page footer.
 * Resolved fresh on every call — nothing is persisted across page views.
 *
 * The footer value prefers `useSMID()`'s React Context when a `SMIDProvider`
 * is present, but falls back to reading the DOM directly. This fallback is
 * required in practice: this hook is consumed by `TrackPageView`,
 * `LinkEventsSubscription`, and `ButtonEventsSubscription`, which all mount
 * above/outside any page-level `SMIDProvider` in the tree (in the root
 * layout/template), so a Context value alone would never reach them.
 *
 * @returns The `smid` context entity, or `null` when neither source has a
 * value, so callers can skip attaching an empty context to their event.
 */
export function useSmidContext(): SelfDescribingJson | null {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const smidPageFooterContext = useSMID();
  const [smidPageFooterDom, setSmidPageFooterDom] = useState<string | null>(
    null,
  );
  const smidUrlParam = searchParams.get(SMID_URL_PARAM_KEY);
  const smidPageFooter = smidPageFooterContext ?? smidPageFooterDom;

  useLayoutEffect(() => {
    // Re-read on every navigation, since the footer's rendered content
    // changes on route change without necessarily remounting this hook.
    setSmidPageFooterDom(readSmidFromFooterDom());
  }, [pathname]);

  return useMemo(() => {
    if (!smidUrlParam && !smidPageFooter) {
      return null;
    }

    return buildSmidContext({ smidUrlParam, smidPageFooter });
  }, [smidUrlParam, smidPageFooter]);
}
